import {writable, get} from 'svelte/store';

import I18n from '@js/modules/translations';

import {userStore} from '@js/stores/userStore';
import {requireAccount} from '@js/actions/accountActions';
import {addFavorite, removeFavorite, fetchFavoriteIds} from '@js/actions/favoriteActions';

import Notification from '@js/components/layouts/notification';

function FavoriteStore() {
    const {subscribe, set, update} = writable(new Set());

    let _hydrated = false;
    let _hydrating = null;
    let _userUnsubscribe = null;

    const _replace = (mutator) => update((ids) => {
        const next = new Set(ids);
        mutator(next);
        return next;
    });

    const has = (rideId) => get({subscribe}).has(Number(rideId));

    const hydrate = () => {
        if (_hydrated) return Promise.resolve();
        if (_hydrating) return _hydrating;
        if (!userStore.isConnected) return Promise.resolve();

        _hydrating = fetchFavoriteIds()
            .then((response) => {
                set(new Set((response?.rideIds || []).map(Number)));
                _hydrated = true;
            })
            .finally(() => {
                _hydrating = null;
            });

        return _hydrating;
    };

    const refresh = () => {
        _hydrated = false;
        return hydrate();
    };

    const _runWhenConnected = (action) => {
        if (userStore.isConnected) {
            action();
            return;
        }

        if (_userUnsubscribe) {
            _userUnsubscribe();
            _userUnsubscribe = null;
        }

        // Open the unified account modal; once connected, hydrate the favorites then
        // replay the toggle (in place — no reload).
        _userUnsubscribe = requireAccount('favorites', () => {
            _hydrated = false;
            hydrate().finally(action);
        }, {notify: I18n.t('js.ride.favorites.flash.login_required')});
    };

    const _persist = (rideId, wasFavorited) => {
        const request = wasFavorited ? removeFavorite(rideId) : addFavorite(rideId);

        return request
            .then((response) => {
                if (response?.errors) {
                    _replace((ids) => (wasFavorited ? ids.add(rideId) : ids.delete(rideId)));
                    Notification.error(I18n.t('js.ride.favorites.flash.error'));
                    return;
                }

                Notification.success(I18n.t(wasFavorited ? 'js.ride.favorites.flash.removed' : 'js.ride.favorites.flash.added'));
            })
            .catch(() => {
                _replace((ids) => (wasFavorited ? ids.add(rideId) : ids.delete(rideId)));
            });
    };

    const toggle = (rideId) => {
        const id = Number(rideId);

        _runWhenConnected(() => {
            const wasFavorited = get({subscribe}).has(id);

            _replace((ids) => (wasFavorited ? ids.delete(id) : ids.add(id)));

            _persist(id, wasFavorited);
        });
    };

    const forget = (rideId) => {
        _replace((ids) => ids.delete(Number(rideId)));
    };

    const remember = (rideId) => {
        _replace((ids) => ids.add(Number(rideId)));
    };

    return {
        subscribe,
        has,
        hydrate,
        refresh,
        toggle,
        forget,
        remember
    };
}

export const favoriteStore = FavoriteStore();
